import { Link } from '@tanstack/react-router';
import { ShieldAlert } from 'lucide-react';
import { useSession } from '@/lib/auth';
import { roleLabelTh } from '@/lib/role-label';

/**
 * Persistent banner shown when the current user's role appears in
 * the `auth.required_2fa_roles` setting but they haven't enrolled.
 *
 * Non-blocking by design — this is visibility-only enforcement, not
 * a hard gate. The actual login flow doesn't change; the user can
 * keep working, but the banner stays until they click through to
 * /account and set up 2FA. Pairs naturally with admin visibility on
 * /users (the existing twoFactor filter pill surfaces who hasn't).
 *
 * Why visibility-only: a hard gate would require a login-flow
 * rewrite (forced-setup challenge JWT, mid-login UI, etc.). For a
 * 6-20 person newsroom that all know each other, the social pressure
 * from a persistent visible banner + admin-visible "no 2FA" filter
 * is usually enough; the heavier enforcement can come later if real
 * usage shows the soft version isn't sticking.
 */
export function Required2faBanner() {
  const { user } = useSession();
  if (!user || !user.requires2fa || user.twoFactorEnabled) return null;

  return (
    <div className="border-b border-amber-200 bg-amber-50 px-4 py-2.5 text-sm dark:border-amber-900/60 dark:bg-amber-950/40">
      <div className="flex items-start gap-3">
        <ShieldAlert className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-600 dark:text-amber-400" />
        <div className="min-w-0 flex-1">
          <p className="font-medium text-amber-900 dark:text-amber-100">บัญชีของคุณต้องเปิด 2FA</p>
          <p className="text-xs text-amber-800 dark:text-amber-200/80">
            ผู้ดูแลระบบบังคับให้บทบาท{' '}
            <span className="font-medium">{roleLabelTh(user.role.slug)}</span> เปิด 2FA —
            โปรดตั้งค่าโดยเร็ว
          </p>
        </div>
        <Link
          to="/account"
          search={{ setup: '2fa' }}
          className="flex-shrink-0 rounded-md bg-amber-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-amber-700 dark:bg-amber-500 dark:text-amber-950 dark:hover:bg-amber-400"
        >
          ตั้งค่า 2FA
        </Link>
      </div>
    </div>
  );
}
